import React, { useState } from 'react';
import { Helmet } from 'react-helmet'; // SEO Added
import { Ruler, Compass, Map, Layers, FileCheck, BrainCircuit, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/archplan.jpg";
import img2 from "../../assets/archplan1.jpg";
import img3 from "../../assets/archplan2.jpg";
import img4 from "../../assets/archplan3.jpg";
import img5 from "../../assets/archplan5.jpg";

const plans = [
  { 
    id: 1, 
    title: 'Architectural Blueprints', 
    category: 'Drafting', 
    image: img1,
    desc: 'Precision CAD layouts maximizing carpet area efficiency according to local bylaws.'
  },
  { 
    id: 2, 
    title: '3D Elevation Models', 
    category: 'Visualisation', 
    image: img2,
    desc: 'Photorealistic front elevation designs (Modern, Classical, & Fusion styles).' 
  },
  { 
    id: 3, 
    title: 'Urban Landscape Planning', 
    category: 'Exterior', 
    image: img3,
    desc: 'Garden, terrace, and driveway landscaping integrated seamlessly with the structure.' 
  },
  { 
    id: 4, 
    title: 'Structural Analysis', 
    category: 'Engineering', 
    image: img4,
    desc: 'Load-bearing calculations and column placement for earthquake resistance.' 
  },
  { 
    id: 5, 
    title: 'Vastu Compliant Maps', 
    category: 'Traditional', 
    image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80",
    desc: 'Scientific Vastu layouts ensuring the "Brahmasthan" and entrances align for prosperity.' 
  },
  { 
    id: 6, 
    title: 'Commercial Complex Design', 
    category: 'Commercial', 
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80",
    desc: 'High-yield retail and office layouts optimized for heavy footfall and visibility.' 
  },
];

const ArchitecturePlanning = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // SEO Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Architectural Design",
    "provider": {
      "@type": "Organization",
      "name": "Luxuriya Homes"
    },
    "areaServed": ["Noida", "Delhi", "Lucknow", "Varanasi"],
    "description": "Professional architectural planning, map approval, and 3D elevation services."
  };

  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* SEO HEAD */}
      <Helmet>
        <title>Best Architects & Map Approval in Noida, Delhi & Lucknow | Luxuriya Homes</title>
        <meta name="description" content="Expert architectural planning, 3D front elevation, and authority map approval services (Noida/LDA). Vastu-compliant house maps and structural engineering." />
        <meta name="keywords" content="architects in noida, map approval lucknow, 3d elevation design, vastu house map, structural engineer delhi, lda approved maps" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* 1. HERO SECTION */}
      <div
        className="relative h-[75vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-4">
             Blueprint for Perfection
          </h4>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight">
             Architectural Planning <br/> & Design
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
             Designing the future skyline of <strong>Noida, Delhi & Lucknow</strong>. <br/>
             From concept to concrete.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-24 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Precision & Perspective
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          Before the first stone is laid, the vision exists on paper. At <strong>Luxuriya Homes</strong>, we provide comprehensive architectural services including 
          Map Sanctioning (Noida Authority/LDA/VDA), structural drawings, and MEP (Mechanical, Electrical, Plumbing) planning. 
          Our designs ensure your building is not just beautiful but compliant, safe, and durable.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">Planning Portfolio</h3>
             <p className="text-gray-500">Explore our residential and commercial blueprints</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((item) => (
              <div key={item.id} className="group relative overflow-hidden shadow-xl cursor-pointer rounded-sm bg-white hover:-translate-y-2 transition-transform duration-300">
                <div className="h-72 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={`${item.title} - Architecture Design Service`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="p-8 border-b-4 border-transparent group-hover:border-amber-500 transition-all">
                  <p className="text-amber-600 text-xs tracking-widest uppercase mb-2 font-bold">{item.category}</p>
                  <h4 className="text-xl font-serif text-gray-900 mb-3">{item.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. TECHNICAL FEATURES & PROCESS */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
           <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">Why Choose Our Architects?</h3>
              <p className="text-gray-400">We blend advanced technology with practical construction knowledge.</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <div className="flex flex-col items-center">
                <Ruler className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Space Optimization</h4>
                <p className="text-gray-400 leading-relaxed">Zero-wastage layouts. We make a 200 sq. yard plot feel like 300 sq. yards.</p>
              </div>
              <div className="flex flex-col items-center">
                <Compass className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Vastu Science</h4>
                <p className="text-gray-400 leading-relaxed">Expert Vastu consultants ensure the 'Brahmasthan' and entrances align perfectly.</p>
              </div>
              <div className="flex flex-col items-center">
                <Map className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Authority Approvals</h4>
                <p className="text-gray-400 leading-relaxed">We handle the paperwork for map sanctioning with **Noida Authority, LDA & Avas Vikas**.</p>
              </div>
              
              {/* Row 2 */}
              <div className="flex flex-col items-center mt-8">
                <Layers className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">MEP Planning</h4>
                <p className="text-gray-400 leading-relaxed">Detailed Electrical, Plumbing, and HVAC drawings to avoid future breakage and seepage.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <FileCheck className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Cost Estimation</h4>
                <p className="text-gray-400 leading-relaxed">Accurate BOQ (Bill of Quantities) so you know the construction cost upfront.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <BrainCircuit className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Structural Safety</h4>
                <p className="text-gray-400 leading-relaxed">Designed by certified structural engineers for maximum durability and earthquake safety.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 5. FAQ SECTION (NEW) */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
           <h3 className="text-3xl font-serif font-bold text-center mb-12">Common Questions</h3>
           <div className="space-y-4">
              {[
                { q: "Do you help with map approval from authorities?", a: "Yes, we handle the entire process of map submission and sanctioning with local authorities in Delhi NCR and UP." },
                { q: "What is the difference between 2D and 3D design?", a: "2D design is the floor plan showing layout and dimensions. 3D design is a photorealistic image of how the building will look from outside (Elevation) or inside." },
                { q: "Do you provide structural drawings?", a: "Absolutely. Our package includes structural details like column placement, beam sizes, and steel reinforcement details." }
              ].map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                   <button 
                     onClick={() => toggleFaq(index)}
                     className="w-full flex justify-between items-center p-6 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                   >
                      <span className="font-bold text-gray-800">{faq.q}</span>
                      {openFaq === index ? <ChevronUp size={20} className="text-amber-600"/> : <ChevronDown size={20} className="text-gray-400"/>}
                   </button>
                   {openFaq === index && (
                     <div className="p-6 bg-white text-gray-600 leading-relaxed border-t border-gray-100">
                        {faq.a}
                     </div>
                   )}
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 px-6 text-center bg-white border-t border-gray-100">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Let's Map Your Vision
        </h2>
        <p className="text-gray-600 mb-10 text-lg max-w-2xl mx-auto">
           Get a consultation for Map Approval or 3D Elevation Design in UP & Delhi.
        </p>
        <Link to="/contact" className="inline-block bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-lg uppercase tracking-widest">
           Start Planning
        </Link>
      </section>
    </div>
  );
};

export default ArchitecturePlanning;